import { useMutation, useQueryClient } from '@tanstack/react-query';
import { joinCommunity, releaseSpaceJoinHold } from '@api-client/api-client';
import { toast } from '@hooks/use-toast';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { buildSingleSpacePayload } from '@lib/analytics/payloads';
import { eventJoinHoldsKey } from '@lib/cache/events';
import { retryUnlessClientError } from '@lib/mutationRetry';
import type { PinRequiredSpace } from '@models/api';
import {
  getErrorCode,
  getErrorStatus,
  JOIN_ALREADY_MEMBER_ERROR_CODE,
  JOIN_INVITE_LINK_REQUIRED_ERROR_CODE,
  JOIN_REQUEST_NOT_PENDING_ERROR_CODE,
  JOIN_REQUEST_PENDING_ERROR_CODE,
} from '@utils/errorDetection';
import { invalidateGoingEventQueries } from '@utils/invalidateGoingEventQueries';
import { invalidateSpaceMembershipQueries } from '@utils/invalidateSpaceMembershipQueries';
import { logger } from '@utils/logger';
import { isWrongPin, joinFailureToast } from '@utils/membershipFailureCopy';

/** What a PIN sent from the Going PIN sheet (TECH-663) came to. */
export type EventPinJoinOutcome =
  | 'joined'
  | 'pin_pending'
  | 'wrong_pin'
  | 'rate_limited'
  /** The Space is unlisted and needs an invite link: the PIN cannot be used here. */
  | 'unlisted'
  /** A host approved the request while the card was open: joined, but not through the PIN. */
  | 'already_member'
  /** The Space's PIN was turned off while the card was open: the request goes to the host. */
  | 'pin_off'
  /** A host decided the request (or it is gone) while the card was open: nothing left to send. */
  | 'decided'
  | 'error';

/** Where the PIN sheet was opened from: a Going tap, or the Event page reopening it from a hold. */
export type EventPinJoinSurface = 'event_going' | 'event_page_reopen';

/** What `joinWithPin` resolves to: the outcome, and for `error` what failed, for its toast. */
export type EventPinJoinResult =
  { outcome: Exclude<EventPinJoinOutcome, 'error'> } | { outcome: 'error'; error: unknown };

/**
 * POST the PIN and classify the answer. Never rejects: a failure the sheet has no branch for is
 * `error`, carrying what failed.
 *
 * - 201 `approved` → joined; 201 `pending` → pin_pending (manual-approval Space, D5). Before
 *   TECH-663 that PIN was a 409 JOIN_REQUEST_PENDING; this sheet only exists against a backend
 *   that sends `pin_required`, which answers it 201.
 * - 403 JOIN_PIN_INVALID → wrong_pin, and so is a 403 with no code or one this client does not
 *   know, as on every PIN surface (`isWrongPin`).
 * - ANY 429 → rate_limited, with or without JOIN_PIN_RATE_LIMITED (D3): a gateway or proxy limit
 *   gets the same "too many tries" answer as the PIN limit.
 * - 403 JOIN_INVITE_LINK_REQUIRED → unlisted.
 * - 409 JOIN_ALREADY_MEMBER → already_member: a host approved the request while the card was open.
 * - 409 JOIN_REQUEST_PENDING → pin_off: the PIN was turned off (the backend checks the PIN only
 *   on a PIN Space), so the request stays a request.
 * - 409 JOIN_REQUEST_NOT_PENDING → decided: a host decided the request, or the row is gone.
 * - anything else → error: a 5xx, no connection, a 404 (the Space is gone), a code this client
 *   does not know, or a 201 with a status it does not know.
 */
export async function joinWithPin(spaceId: string, pin: string): Promise<EventPinJoinResult> {
  try {
    const response = await joinCommunity(spaceId, { access_code: pin });
    const { status } = response.data;
    if (status === 'approved') return { outcome: 'joined' };
    if (status === 'pending') return { outcome: 'pin_pending' };
    return { outcome: 'error', error: new Error(`Unexpected join status: ${status}`) };
  } catch (error) {
    const httpStatus = getErrorStatus(error);
    const code = getErrorCode(error);
    if (httpStatus === 429) return { outcome: 'rate_limited' };
    if (httpStatus === 403 && code === JOIN_INVITE_LINK_REQUIRED_ERROR_CODE) {
      return { outcome: 'unlisted' };
    }
    // Read as every PIN surface reads it: any other 403 to a join that carried a PIN is a wrong
    // PIN, JOIN_PIN_INVALID or not (`isWrongPin`, membershipFailureCopy.ts).
    if (isWrongPin(error, { accessCode: pin })) return { outcome: 'wrong_pin' };
    if (httpStatus === 409 && code === JOIN_ALREADY_MEMBER_ERROR_CODE) {
      return { outcome: 'already_member' };
    }
    if (httpStatus === 409 && code === JOIN_REQUEST_PENDING_ERROR_CODE)
      return { outcome: 'pin_off' };
    if (httpStatus === 409 && code === JOIN_REQUEST_NOT_PENDING_ERROR_CODE) {
      return { outcome: 'decided' };
    }
    return { outcome: 'error', error };
  }
}

/**
 * The two network calls of the Going PIN sheet.
 *
 * - `submitPin(space, pin, joinSurface)` joins with the PIN. The caches are swept, and a join is
 *   reported (`space_joined`, the join funnel's success leg, plus the meaningful action), from the
 *   mutation's own `onSuccess`, so both still happen when the sheet has closed before the answer
 *   lands. A 409 JOIN_ALREADY_MEMBER is a host's approval, not a PIN join, so it is not reported
 *   as one. A join
 *   takes the Going settle's sweep (`invalidateGoingEventQueries`: the event feed and counts, plus
 *   the membership sweep for this one Space); a join that may have committed unseen (`error`)
 *   takes the same sweep as `unknown`; a request a host closed meanwhile (`decided`) takes the
 *   membership sweep. A failure that is not about the PIN (`error`) is said here, once, in the
 *   toast every join surface uses (`joinFailureToast`), as useCommunityMembership does.
 *   Sent once and never again: `joinWithPin` resolves every answer, so the mutation never fails
 *   and no retry policy can re-send it. That is stricter than the other joins'
 *   `retryUnlessClientError` (which re-sends a 5xx or a lost connection), on purpose: a re-sent
 *   PIN counts again against the PIN attempt limit, and a retry's backoff would hold the sheet in
 *   `submitting`, with every exit closed. `networkMode: 'always'`: offline, the call fails at once
 *   instead of pausing, for the same reason.
 * - `releaseHold(spaceId)` tells the host now (DELETE join-hold). Fire-and-forget: a failure is
 *   logged, and the server's 10-minute timer releases the hold anyway. It is idempotent (204
 *   whether or not anything was held), so a 5xx or a lost connection is sent again; a 4xx is
 *   final (`retryUnlessClientError`).
 *
 * Both keep the event's join-holds cache (`eventJoinHoldsKey`, which the Event page reopens the
 * sheet from) in step: a Space the sheet has answered for (a request, a right PIN) leaves it at
 * once, and the entry is re-read once the server has moved. A wrong PIN leaves the hold alone.
 */
export function useEventPinJoin(eventId: string) {
  const queryClient = useQueryClient();
  const { trackEvent, trackMeaningfulAction } = useAmplitudeTracking();
  const holdsKey = eventJoinHoldsKey(eventId);

  // Cancels a read already in flight first: it started before this answer and would bring the
  // Space back.
  const dropHold = (spaceId: string) => {
    void queryClient.cancelQueries({ queryKey: holdsKey });
    queryClient.setQueryData<PinRequiredSpace[]>(holdsKey, old =>
      old ? old.filter(space => space.space_id !== spaceId) : old,
    );
  };

  const join = useMutation({
    // `space` and `joinSurface` ride along for the analytics in onSuccess (no stale closure).
    mutationFn: ({
      spaceId,
      pin,
    }: {
      spaceId: string;
      pin: string;
      space: PinRequiredSpace;
      joinSurface: EventPinJoinSurface;
    }) => joinWithPin(spaceId, pin),
    // Never reached (the mutation never fails, see above); kept so that it stays true if it ever
    // does.
    retry: false,
    // Offline, the default networkMode ('online') starts the mutation paused, and its promise
    // waits for the network: the sheet would stay `submitting`, with every exit closed. 'always'
    // sends at once, so an offline PIN fails fast as `error` and the card is usable again.
    networkMode: 'always',
    onSuccess: (result, { spaceId, pin, space, joinSurface }) => {
      const { outcome } = result;
      if (result.outcome === 'error') {
        // Said once, here, whether or not the sheet is still on screen: a toast outlives it. One
        // per Space: another failure (offline, a tap on Join again) replaces it, never stacks.
        toast({
          ...joinFailureToast(result.error, { accessCode: pin }),
          id: `event-pin-join-failed:${spaceId}`,
        });
        // The POST may have committed (a timeout after commit looks like any failure), so the
        // Space is swept as `unknown`; the hold is re-read, not dropped.
        invalidateGoingEventQueries(queryClient, {
          id: eventId,
          spaceIds: [spaceId],
          membershipChange: { kind: 'unknown', candidateIds: [spaceId] },
        });
        return;
      }
      if (outcome === 'pin_pending') {
        // A right PIN on a manual-approval Space: the server released the hold (D5). The
        // membership stays the pending row it was, so nothing else moved.
        dropHold(spaceId);
        void queryClient.invalidateQueries({ queryKey: holdsKey });
        return;
      }
      if (outcome === 'decided') {
        // A host decided the request meanwhile, or it is gone: a release would send nothing
        // (D4), so the hold is done with. The pending row the caches hold is not there any more.
        dropHold(spaceId);
        void queryClient.invalidateQueries({ queryKey: holdsKey });
        invalidateSpaceMembershipQueries(queryClient, {
          change: { kind: 'these', ids: [spaceId] },
          alreadySweptByCaller: {
            eventRoot: false,
            spaceEventLists: false,
            mySpacesFeed: false,
            heldPostPages: false,
          },
        });
        return;
      }
      if (outcome !== 'joined' && outcome !== 'already_member') return;
      if (outcome === 'joined') {
        // Shaped like the Space page's (useCommunityMembership), with the event it came from.
        trackEvent(ANALYTICS_EVENTS.space_joined, {
          ...buildSingleSpacePayload({
            id: space.space_id,
            name: space.space_name,
            slug: space.space_slug,
          }),
          status: 'approved',
          is_approved: true,
          join_method: 'pin',
          join_surface: joinSurface,
          event_id: eventId,
        });
        trackMeaningfulAction('space_joined', { space_id: spaceId, join_method: 'pin' });
      }
      dropHold(spaceId);
      // Sweeps the holds too: they sit under eventKey(eventId).
      invalidateGoingEventQueries(queryClient, {
        id: eventId,
        spaceIds: [spaceId],
        membershipChange: { kind: 'these', ids: [spaceId] },
      });
    },
  });

  const release = useMutation({
    mutationFn: (spaceId: string) => releaseSpaceJoinHold(spaceId),
    // As for the join: never paused offline. A failed release is logged; the server's timer
    // releases the hold anyway.
    networkMode: 'always',
    retry: retryUnlessClientError,
    onMutate: spaceId => {
      dropHold(spaceId);
    },
    onError: (error, spaceId) => {
      logger.warn('[EventPinSheet] join-hold release failed', { spaceId, error });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: holdsKey });
    },
  });

  return {
    submitPin: (space: PinRequiredSpace, pin: string, joinSurface: EventPinJoinSurface) =>
      join.mutateAsync({ spaceId: space.space_id, pin, space, joinSurface }),
    releaseHold: (spaceId: string) => {
      release.mutate(spaceId);
    },
  };
}
